import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Smartphone, Download } from 'lucide-react';
import AuthModal from '../auth/AuthModal';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useAuthStore } from '../../stores/authStore';
import { useUserData } from '../../hooks/useUserData';
import UserProfileDropdown from './UserProfileDropdown';


const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const { isAuthModalOpen, openAuthModal, closeAuthModal } = useAuthModal();
  const { isAuthenticated } = useAuthStore();
  // Use the SAME REUSABLE hook - NO MORE DUPLICATE HOOKS!
  const { userInfo, isLoading } = useUserData();
  const balance = userInfo?.credits || 0;
  


  // Update authentication state when modal closes
  const handleModalClose = () => {
    closeAuthModal();
  };

  return (
    <header className="relative bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 py-2">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200">
              <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                <span className="text-white text-lg font-bold">M</span>
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Metapic</h1>
                <div className="text-xs text-gray-500 font-medium -mt-0.5">AI Metadata Generator</div>
              </div>
            </Link>
          </div>

          {/* Navigation Menu */}
          <nav className="hidden md:flex items-center space-x-1">
            <a 
              href="/upload" 
              onClick={(e) => {
                if (typeof window !== 'undefined' && (window as any).fbq) {
                  (window as any).fbq('track', 'ViewContent', { content_name: 'Upload Page' });
                }
                // Force reload if already on upload page
                if (window.location.pathname === '/upload') {
                  e.preventDefault();
                  window.location.reload();
                }
              }}
              className="px-3 py-1.5 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 text-sm font-medium"
            >
              <span>Upload</span>
            </a>

            <Link 
              to="/my-uploads" 
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).fbq) {
                  (window as any).fbq('track', 'ViewContent', { content_name: 'My Uploads' });
                }
              }}
              className="px-3 py-1.5 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 text-sm font-medium"
            >
              <span>My Uploads</span>
            </Link>

            <Link 
              to="/csv-editor" 
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).fbq) {
                  (window as any).fbq('track', 'ViewContent', { content_name: 'CSV Editor' });
                }
              }}
              className="px-3 py-1.5 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 text-sm font-medium"
            >
              <span>CSV Editor</span>
            </Link>

            <Link 
              to="/pricing" 
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).fbq) {
                  (window as any).fbq('track', 'ViewContent', { content_name: 'Pricing Page' });
                }
              }}
              className="px-3 py-1.5 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 text-sm font-medium"
            >
              <span>Pricing</span>
            </Link>
            
            {/* Credit Counter & Auth Button */}
            <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200">
              {isAuthenticated && (
                <div className="flex flex-col items-center space-y-0.5">
                  {isLoading ? (
                    <div className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full border bg-gray-100 border-gray-200">
                      <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-pulse"></div>
                      <div className="h-3 w-12 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  ) : (
                    <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full border ${
                      balance === 0
                        ? 'bg-red-50 border-red-200'
                        : balance <= 50
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-green-50 border-green-200'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                        balance === 0
                          ? 'bg-red-500'
                          : balance <= 50
                            ? 'bg-yellow-500'
                            : 'bg-green-500'
                      }`}></div>
                      <span className={`text-xs font-semibold ${
                        balance === 0
                          ? 'text-red-700'
                          : balance <= 50
                            ? 'text-yellow-700'
                            : 'text-green-700'
                      }`}>
                        {balance} credits
                      </span>
                    </div>
                  )}
                </div>
              )}

              {isAuthenticated ? (
                 <UserProfileDropdown />
               ) : (
                 <button
                   onClick={openAuthModal}
                   className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md font-medium text-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm hover:shadow-md"
                 >
                   Log In
                 </button>
               )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200/50 bg-white/95 backdrop-blur-md">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/pricing"
              className="block w-full px-3 py-2.5 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>Pricing</span>
            </Link>

            <a
              href="/upload"
              onClick={(e) => {
                // Force reload if already on upload page
                if (window.location.pathname === '/upload') {
                  e.preventDefault();
                  window.location.reload();
                } else {
                  setIsMobileMenuOpen(false);
                }
              }}
              className="block w-full px-3 py-2.5 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium text-sm"
            >
              <span>Upload</span>
            </a>

            <Link
              to="/my-uploads"
              className="block w-full px-3 py-2.5 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>My Uploads</span>
            </Link>

            <Link
              to="/csv-editor"
              className="block w-full px-3 py-2.5 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 font-medium text-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>CSV Editor</span>
            </Link>
            
            {isAuthenticated && (
              <div className="px-4 py-2">
                {isLoading ? (
                  <div className="flex items-center space-x-1.5 px-3 py-1.5 rounded-full border bg-gray-100 border-gray-200 w-fit">
                    <div className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-pulse"></div>
                    <div className="h-3 w-12 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                ) : (
                  <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full border w-fit ${
                    balance === 0
                      ? 'bg-red-50 border-red-200'
                      : balance <= 50
                        ? 'bg-yellow-50 border-yellow-200'
                        : 'bg-green-50 border-green-200'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                      balance === 0
                        ? 'bg-red-500'
                        : balance <= 50
                          ? 'bg-yellow-500'
                          : 'bg-green-500'
                    }`}></div>
                    <span className={`text-xs font-semibold ${
                      balance === 0
                        ? 'text-red-700'
                        : balance <= 50
                          ? 'text-yellow-700'
                          : 'text-green-700'
                    }`}>
                      {balance} credits
                    </span>
                  </div>
                )}
              </div>
            )}

            {isAuthenticated ? (
              <div className="px-4 py-2">
                <UserProfileDropdown className="w-full" isMobile={true} />
              </div>
            ) : (
              <button
                onClick={openAuthModal}
                className="block w-full px-3 py-2 mt-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md font-medium text-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-sm"
              >
                Log In
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={handleModalClose} 
      />

      {/* Mobile App Download Modal */}
      {showMobileModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowMobileModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowMobileModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Modal Content */}
            <div className="text-center">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Get Metapic Mobile App
                </h2>
                <p className="text-gray-600">
                  Coming Soon to Google Play Store
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6 mb-6">
                <p className="text-sm text-gray-700 mb-4">
                  Our app is currently being reviewed by Google. If you want to try the app, you can download the APK directly or scan the QR code below.
                </p>
                
                {/* QR Code */}
                <div className="bg-white rounded-lg p-4 inline-block">
                  <img 
                    src="https://metapic-desktop-releases.s3.us-east-1.amazonaws.com/qr-code-app.png" 
                    alt="Download APK QR Code" 
                    className="w-48 h-48 mx-auto"
                  />
                </div>
              </div>

              {/* Download Button */}
              <a
                href="https://metapic-desktop-releases.s3.us-east-1.amazonaws.com/metapic-v1.3.2.apk"
                className="inline-flex items-center justify-center w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors duration-200 shadow-lg hover:shadow-xl mb-4"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="w-5 h-5 mr-2" />
                Download APK (v1.3.2)
              </a>

              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong className="text-gray-900">⚠️ Testing Version:</strong> This APK is provided for testing purposes. For official download from Google Play, please <a href="/contact" className="text-blue-600 hover:underline font-medium">contact us</a> and provide your email address. We will invite you to our closed beta so you can download directly from Google Play.
                </p>
              </div>

              <p className="text-xs text-gray-500 mt-3">
                Note: You may need to enable "Install from Unknown Sources" in your Android settings
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;